import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { user } from '../../auth'
import { Book } from '../../content'

// =============================================================================
// READING HISTORY DAILY - Lookup table (generated by background function)
// Stores aggregated reading history data by user, book, and date
// Used for displaying "riwayat baca" (reading history) feature
// =============================================================================

export const ReadingHistoryDaily = pgTable(
  'reading_history_daily',
  {
    id: uuid().defaultRandom().primaryKey(),
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    bookId: uuid()
      .references(() => Book.id, { onDelete: 'cascade' })
      .notNull(),
    date: timestamp({ mode: 'date', withTimezone: true }).notNull(), // The specific date (normalized to start of day)

    // Daily aggregated metrics per book
    totalReadingSeconds: integer().notNull().default(0), // Sum of all session durations for this book on this day
    sessionCount: integer().notNull().default(0), // Number of reading sessions for this book on this day
    isCompleted: boolean().notNull().default(false), // Whether the book was completed on this day

    // --- Membership snapshot (the "Laporan" dimension) --------------------------------------
    // The school context the learner was in AT ROLL-UP TIME, stamped by the producer on every write.
    // A report ("Laporan baca kelas 1 A") groups on these, so it keeps meaning the members of 1 A in
    // that period even after a student is moved to another class or promoted to the next grade.
    // Every column is nullable: a child who only belongs to a family organization has no school
    // context. A child can be a member of two organizations at once (family + school), which is
    // exactly why the organization is recorded here instead of being re-derived at read time.
    // NOT FOREIGN KEYS, deliberately. A snapshot must carry no referential dependency at all: even
    // `ON DELETE SET NULL` erases the value the moment a school or a class is deleted, which is the
    // history loss the snapshot exists to prevent. Plain text, resolved by the producer via
    // resolveMetricSnapshotContexts (packages/drizzle/src/queries/metric-snapshot-context.ts).
    // IDS ONLY — no name/avatar columns on this table. Its grain multiplies (user, day) by
    // a BOOK, so repeating a name and two URLs on every row would bloat one of the biggest
    // tables in the set for nothing: a Laporan aggregates from the (user, day)-grain tables, which
    // do carry the labels, and these ids are still enough to filter by school/class and to join
    // back to those rows.
    schoolOrganizationId: text(),
    // The "Kelas" / rombel. Always a team of schoolOrganizationId above.
    schoolTeamId: text(),
    // The "Tingkat" / grade, mirroring user.schoolClass — a plain text column, not a DB enum.
    // No separate label: it already IS the human-readable value, not an id.
    schoolClass: text(),

    // Tracking fields
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_reading_history_daily_user').on(t.userId),
    index('idx_reading_history_daily_book').on(t.bookId),
    index('idx_reading_history_daily_date').on(t.date),
    index('idx_reading_history_daily_user_date').on(t.userId, t.date),
    index('idx_reading_history_daily_user_book_date').on(t.userId, t.bookId, t.date),
    // Reporting reads: every row a school produced in a period, then narrowed to one class.
    index('idx_reading_history_daily_school_org_date').on(t.schoolOrganizationId, t.date),
    index('idx_reading_history_daily_school_team_date').on(t.schoolTeamId, t.date),
    unique('uq_reading_history_daily_user_book_date').on(t.userId, t.bookId, t.date),
  ],
)

export const readingHistoryDailyRelations = relations(ReadingHistoryDaily, ({ one }) => ({
  user: one(user, {
    fields: [ReadingHistoryDaily.userId],
    references: [user.id],
  }),
  book: one(Book, {
    fields: [ReadingHistoryDaily.bookId],
    references: [Book.id],
  }),
}))

export const InsertReadingHistoryDailySchema = createInsertSchema(ReadingHistoryDaily).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateReadingHistoryDailySchema = createUpdateSchema(ReadingHistoryDaily).partial().omit({
  id: true,
  userId: true,
  bookId: true,
  date: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectReadingHistoryDailySchema = createSelectSchema(ReadingHistoryDaily)

export type InsertReadingHistoryDaily = typeof ReadingHistoryDaily.$inferInsert
export type UpdateReadingHistoryDaily = z.infer<typeof UpdateReadingHistoryDailySchema>
export type SelectReadingHistoryDaily = typeof ReadingHistoryDaily.$inferSelect
